import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import { calculateActualAmount, calculateTotal, calculateTransactionFee, cartItemsPayment, updatePriceField } from '@/helpers/order-redux'
import { loadFromStorage, saveToStorage } from '@/hooks/useLocalStorage'
import { CartInfo, CartItem, CartItemsPayment, Orders, PaymentMethod } from '@/types/cart.type'
import { PaymentCode } from '@/types/enum'
import { calQty, formatedAddress } from '@/lib/utils'

const orderTemplate: Orders = {
  delivery_method_id: undefined,
  delivery_address_id: -1,
  payment_method_id: -1,
  address: '',
  location_id: undefined,
  transport_fee: 0,
  transaction_fee: 0,
  vat: undefined,
  taxvalue: undefined,
  use_bonus: 0,
  consignee_pay_fee: undefined,
  delivery_global_id: undefined,
  company_name: undefined,
  taxcode: undefined,
  company_address: undefined,
  content: undefined,
  carts_items: [],
  payment_code: '',
}

const order = orderTemplate

const cartItems = loadFromStorage('cart') ?? []

const initialState: {
  cartItems: CartItem[]
  totalMoney: number
  actualAmount: number
  taxFee: number
  isNew: boolean
  cartItemsPayment: CartItemsPayment[]
  order: Orders
  payments: PaymentMethod[]
  vatError: Record<string, string>
  timePickup?: string
  datePickup?: string
} = {
  cartItems: updatePriceField(cartItems, Boolean(order.delivery_global_id) == true),
  totalMoney: calculateTotal(updatePriceField(cartItems, Boolean(order.delivery_global_id) == true)),
  actualAmount: calculateActualAmount(updatePriceField(cartItems, Boolean(order.delivery_global_id) == true)),
  taxFee: 0,
  isNew: false,
  cartItemsPayment: cartItemsPayment(cartItems),
  order: order,
  payments: [],
  vatError: {},
  timePickup: undefined,
  datePickup: undefined,
}

const validDeliveryMethods = (cartInfo: CartInfo) => {
  if (!cartInfo || Array.isArray(cartInfo) || !cartInfo.delivery_methods) {
    return []
  }

  return cartInfo.delivery_methods
    .filter((dm) => {
      if (dm.used_for_all) {
        return true
      }
      return dm.locations.some((dml) => dml.location_id === cartInfo.delivery_address.province_id)
    })
    .sort((a, b) => a.sorted - b.sorted)
}

const orderSlice = createSlice({
  name: 'order',
  initialState,
  reducers: {
    /**
     * cập nhật đơn hàng khi vừa mounted
     */
    setOrder: (state, action: PayloadAction<CartInfo>) => {
      const payload = action.payload
      const deliveryAddress = payload.delivery_address
      const deliveryMethods = validDeliveryMethods(payload) || []
      const deliveryGlobals = payload.delivery_globals || []
      const paymentMethods = payload.payment_methods || []
      const company = payload.company || {}
      const settings = payload.settings || {}

      const hasDeliveryGlobal = deliveryGlobals.length !== 0
      const firstDeliveryMethod = deliveryMethods[0]
      const addressFormated = formatedAddress(deliveryAddress?.address || '')

      // Định dạng địa chỉ dựa trên loại giao hàng
      const address = hasDeliveryGlobal
        ? `${addressFormated}, ${deliveryAddress?.city}, ${deliveryAddress?.state} ${deliveryAddress?.country?.name}`
        : `${addressFormated}, ${deliveryAddress?.ward?.name}${deliveryAddress?.district_id ? deliveryAddress?.district?.name : ''}, ${deliveryAddress?.province?.name}`

      let transportFee = 0
      let locationId = undefined
      let paymentMethodId = -1
      let transactionFee = 0

      if (!hasDeliveryGlobal && firstDeliveryMethod) {
        switch (firstDeliveryMethod.payment_level) {
          case 1:
            locationId = deliveryAddress?.province_id
            transportFee = deliveryAddress?.province?.payment ?? 0
            break
          // case 2:
          //   locationId = deliveryAddress?.district_id
          //   transportFee = deliveryAddress?.district?.payment ?? 0
          //   break
          case 3:
            locationId = deliveryAddress?.ward_id
            transportFee = deliveryAddress?.ward?.payment ?? 0
            break
          default:
            transportFee = 0
            break
        }
      }

      // Chọn phương thức thanh toán phù hợp
      const selectedPayments = hasDeliveryGlobal
        ? paymentMethods
        : paymentMethods.filter((pm) => deliveryMethods.some((dm) => dm.payment_methods?.some((method) => method.payment_method_id === pm.id)))

      selectedPayments.sort((a, b) => a.sorted - b.sorted)

      if (selectedPayments.length > 0) {
        paymentMethodId = selectedPayments[0].id
        // transactionFee = (selectedPayments[0].payment_fee ?? 0) + (state.totalMoney * (selectedPayments[0].payment_fee_percent ?? 0)) / 100
        transactionFee = (selectedPayments[0].payment_fee ?? 0) + (state.totalMoney * 1) / 100
      }

      // ahamove
      const extBody = {} as any

      if (firstDeliveryMethod?.id !== 5) {
        extBody.third_party_service_id = undefined
        extBody.estimated_fee = undefined
      }

      // Cập nhật state.order một lần duy nhất
      state.order = {
        ...state.order,
        ...extBody,
        delivery_address_id: deliveryAddress?.id,
        taxvalue: settings.tax ? +settings.tax : 0,
        consignee_pay_fee: 0,
        company_name: company.name ?? '',
        company_address: company.address ?? '',
        delivery_method_id: firstDeliveryMethod?.id,
        use_bonus: 0,
        vat: 0,
        address,
        transport_fee: transportFee,
        payment_method_id: paymentMethodId,
        transaction_fee: transactionFee,
        location_id: locationId,
        ...(hasDeliveryGlobal && {
          delivery_global_id: undefined,
          payment_code: paymentMethods[0]?.payment_code ?? '',
        }),
      }

      const deliveryPayments = deliveryMethods.find((dp) => dp.id === state.order.delivery_method_id)
      const paymentCashId = paymentMethods.find((pm) => pm.payment_code === PaymentCode.cash)?.id
      let allowedPaymentIds = new Set(deliveryPayments?.payment_methods.map((pm) => pm.payment_method_id) || [])

      // nếu vận chuyển bằng xe ôm thì không cho phép thanh toán bằng tiền mặt
      // id == 4 là xe ôm trong db delivery_methods
      if (deliveryPayments?.id === 4 && paymentCashId) {
        allowedPaymentIds = allowedPaymentIds.delete(paymentCashId) ? allowedPaymentIds : new Set(allowedPaymentIds)
      }

      state.payments = paymentMethods.filter((payment) => allowedPaymentIds.has(payment.id)).sort((a, b) => a.sorted - b.sorted)

      // Lưu vào sessionStorage
      if (typeof window !== 'undefined') {
        sessionStorage.setItem('order', JSON.stringify(state.order))
      }
    },
    /**
     * cập nhật đơn hàng theo thao tác người dùng
     */
    updateOrder: (state, action: PayloadAction<{ order: Orders; vat: string; type?: string }>) => {
      const { order, vat, type } = action.payload
      state.order = { ...order }
      state.order.taxvalue = Number.isFinite(+vat) ? +vat : 0

      if (type !== 'at_shop') {
        const needsTransactionFeeUpdate = !state.order.transaction_fee || order.delivery_method_id
        const transactionFee = needsTransactionFeeUpdate ? Math.round(calculateTransactionFee(state.payments, state.order, state.cartItems)) : 0

        if (order.vat === 1) {
          if (order.payment_code === PaymentCode.atm) {
            state.order.transaction_fee = 0
          } else {
            state.order.transaction_fee = transactionFee
          }
        }
      }

      state.taxFee = Math.round((state.actualAmount + (state.order.transport_fee ?? 0)) * +vat)

      // Lưu vào sessionStorage nếu có window
      if (typeof window !== 'undefined') {
        sessionStorage.setItem('order', JSON.stringify(state.order))
      }
    },
    /**
     * ghi chú đơn hàng
     */
    updateOrderNote: (state, action: PayloadAction<string>) => {
      state.order = { ...state.order, content: action.payload }
      // Lưu vào sessionStorage nếu có window
      if (typeof window !== 'undefined') {
        sessionStorage.setItem('order', JSON.stringify(state.order))
      }
    },
    /**
     * cập nhật phương thức thanh toán
     */
    updatePayments: (state, action: PayloadAction<CartInfo>) => {
      const { delivery_methods, payment_methods } = action.payload

      const deliveryPayments = delivery_methods.find((dp) => dp.id === state.order.delivery_method_id)
      const paymentCashId = payment_methods.find((pm) => pm.payment_code === PaymentCode.cash)?.id
      let allowedPaymentIds = new Set(deliveryPayments?.payment_methods.map((pm) => pm.payment_method_id) || [])

      // nếu vận chuyển bằng xe ôm thì không cho phép thanh toán bằng tiền mặt
      // id == 4 là xe ôm trong db delivery_methods
      if (deliveryPayments?.id === 4 && paymentCashId) {
        allowedPaymentIds = allowedPaymentIds.delete(paymentCashId) ? allowedPaymentIds : new Set(allowedPaymentIds)
      }

      state.payments = payment_methods.filter((payment) => allowedPaymentIds.has(payment.id)).sort((a, b) => a.sorted - b.sorted)
    },
    /**
     * cập nhật sản phẩm muốn mua
     */
    updateCheck: (state, action: PayloadAction<{ id: number }>) => {
      let updateCartItems = [...state.cartItems]

      switch (action.payload.id) {
        case 1:
          updateCartItems = updateCartItems.map((cart: CartItem) => ({ ...cart, isCheck: true }))
          break
        case -1:
          const check = state.cartItems.every((item) => item.isCheck === true)
          updateCartItems = updateCartItems.map((cart: CartItem) => ({ ...cart, isCheck: check ? false : true }))
          break
        default:
          updateCartItems = updateCartItems.map((cart: CartItem) => (cart.id === action.payload.id ? { ...cart, isCheck: !cart.isCheck } : cart))
          break
      }

      // Update the state in one place
      state.cartItems = updateCartItems
      state.actualAmount = calculateActualAmount(updateCartItems)
      state.cartItemsPayment = cartItemsPayment(updateCartItems)
      saveToStorage(updateCartItems, 'cart')

      // Recalculate transaction fee
      const transactionFee = calculateTransactionFee(state.payments, state.order, updateCartItems)
      state.order = { ...state.order, transaction_fee: transactionFee }
    },
    /**
     * Xóa sản phẩm khỏi giỏ hàng
     */
    removeItem: (state, action: PayloadAction<{ id: number }>) => {
      // Filter out the removed item from both cartItems and cartItemsPayment
      const updatedCartItems = state.cartItems.filter((cart: CartItem) => cart.id !== action.payload.id)
      const updatedCartItemsPayment = state.cartItemsPayment.filter((cart: CartItemsPayment) => cart.product_id !== action.payload.id)

      // Update the state once
      state.cartItems = updatedCartItems
      state.cartItemsPayment = updatedCartItemsPayment
      state.totalMoney = calculateTotal(updatedCartItems)
      state.actualAmount = calculateActualAmount(updatedCartItems)
      saveToStorage(updatedCartItems, 'cart')

      // Recalculate transaction fee
      const transactionFee = calculateTransactionFee(state.payments, state.order, updatedCartItems)
      state.order = { ...state.order, transaction_fee: transactionFee }
    },
    /**
     * thêm sản phẩm vào giỏ hàng
     */
    addItem: (state, action: PayloadAction<{ cartItem: CartItem; isNew: boolean }>) => {
      const check = state.cartItems.some((item) => item.id === action.payload.cartItem.id)

      // Define updated cartItems based on the check result
      let updatedCartItems

      if (check) {
        updatedCartItems = state.cartItems.map((item) => (item.id === action.payload.cartItem.id ? { ...item, quantity: item.quantity + action.payload.cartItem.quantity } : item))
      } else {
        updatedCartItems = [...state.cartItems, { ...action.payload.cartItem, isCheck: action.payload.cartItem.isCheck ?? false }]
      }
      // Update the cart items and perform calculations in one place
      state.cartItems = updatePriceField(updatedCartItems, !!state.order.delivery_global_id)
      state.totalMoney = calculateTotal(state.cartItems)
      state.actualAmount = calculateActualAmount(state.cartItems)
      state.isNew = action.payload.isNew
      state.cartItemsPayment = check ? state.cartItemsPayment : cartItemsPayment(state.cartItems)
      saveToStorage(state.cartItems, 'cart')
    },
    /**
     * thêm sản phẩm vào giỏ hàng khi đăng nhập
     */
    storeWhenLogin: (state, action: PayloadAction<{ cartItem: CartItem; hideProductIds: number[] }>) => {
      const { cartItem, hideProductIds } = action.payload

      // Lọc bỏ những cartItem có id nằm trong hideProductIds
      const filteredCartItems = state.cartItems.filter((item) => !hideProductIds.includes(item.id))

      const check = filteredCartItems.some((item) => item.id === cartItem.id)

      let updatedCartItems
      if (check) {
        updatedCartItems = filteredCartItems.map((item) => (item.id === cartItem.id ? { ...item, ...cartItem, quantity: cartItem.quantity, inventory: cartItem.inventory } : item))
      } else {
        updatedCartItems = [...filteredCartItems, { ...cartItem, isCheck: false }]
      }

      // Update cartItems and perform necessary calculations
      state.cartItems = updatePriceField(updatedCartItems, !!state.order.delivery_global_id)
      state.totalMoney = calculateTotal(state.cartItems)
      state.actualAmount = calculateActualAmount(state.cartItems)
      state.cartItemsPayment = check ? state.cartItemsPayment : cartItemsPayment(state.cartItems)
      saveToStorage(state.cartItems, 'cart')
    },
    /**
     * mua lại đơn hàng
     */
    storeWhenRebuy: (state, action: PayloadAction<{ cartItems: CartItem[] }>) => {
      const { cartItems } = action.payload
      const rebuyItemsMap = new Map(cartItems.map((item) => [item.id, item]))
      const existingItemIds = new Set(state.cartItems.map((item) => item.id))

      // Update existing items và uncheck các items không có trong rebuy list
      const updatedExistingItems = state.cartItems.map((item) => {
        const rebuyItem = rebuyItemsMap.get(item.id)
        if (rebuyItem) {
          // Merge properties một cách tối ưu
          return {
            ...item,
            ...rebuyItem,
            isCheck: true,
            quantity: rebuyItem.quantity,
            inventory: rebuyItem.inventory,
          }
        }
        return { ...item, isCheck: false }
      })

      // Thêm items mới (chỉ các items chưa tồn tại)
      const newItems = cartItems.filter((item) => !existingItemIds.has(item.id)).map((item) => ({ ...item, isCheck: true }))

      // Combine arrays một lần duy nhất
      const finalCartItems = [...updatedExistingItems, ...newItems]

      // Update state với price fields và calculations
      const cartItemsWithUpdatedPrice = updatePriceField(finalCartItems, !!state.order.delivery_global_id)

      // Batch update tất cả state properties
      state.cartItems = cartItemsWithUpdatedPrice
      state.totalMoney = calculateTotal(cartItemsWithUpdatedPrice)
      state.actualAmount = calculateActualAmount(cartItemsWithUpdatedPrice)
      state.cartItemsPayment = cartItemsPayment(cartItemsWithUpdatedPrice)

      saveToStorage(cartItemsWithUpdatedPrice, 'cart')
    },
    /**
     * cập nhật số lượng sản phẩm
     */
    updateQuantity: (state, action: PayloadAction<{ id: number; type: 'minus' | 'plus' | 'number'; quantity?: number }>) => {
      let updatedCartItems

      switch (action.payload.type) {
        case 'minus':
          updatedCartItems = state.cartItems.map((item) =>
            item.id === action.payload.id ? { ...item, quantity: Math.max(item.minimum_order_quantity, calQty(item.quantity - item.multiples, item.multiples)) } : item,
          )
          break
        case 'plus':
          updatedCartItems = state.cartItems.map((item) => (item.id === action.payload.id ? { ...item, quantity: calQty(item.quantity + item.multiples, item.multiples) } : item))
          break
        case 'number':
          updatedCartItems = state.cartItems.map((item) => (item.id === action.payload.id ? { ...item, quantity: calQty(action.payload.quantity!, item.multiples) } : item))
          break
        default:
          return
      }

      // Update the cart items and perform calculations in one place
      state.cartItems = updatePriceField(updatedCartItems, !!state.order.delivery_global_id)
      state.totalMoney = calculateTotal(state.cartItems)
      state.actualAmount = calculateActualAmount(state.cartItems)
      state.cartItemsPayment = cartItemsPayment(state.cartItems)

      if (state.order.vat === 1) {
        const vatRate = Number(state.order.taxvalue ?? 0)
        state.taxFee = Math.round((state.actualAmount + (state.order.transport_fee ?? 0)) * vatRate)
      } else {
        state.taxFee = 0
      }

      saveToStorage(state.cartItems, 'cart')

      // Recalculate the transaction fee
      const transactionFee = calculateTransactionFee(state.payments, state.order, state.cartItems)
      state.order = { ...state.order, transaction_fee: transactionFee }
    },
    /**
     * Cập nhật category_extra_id (cuộn | không)
     */
    updateCategoryExtraId: (state, action: PayloadAction<{ cartId: number; categoryExtraId: number }>) => {
      // Update the category_extra_id for the specific cart item
      state.cartItems = state.cartItems.map((cart: CartItem) => (cart.id === action.payload.cartId ? { ...cart, category_extra_id: action.payload.categoryExtraId } : cart))

      // Perform necessary recalculations and save state once
      const updatedCartItems = state.cartItems
      state.actualAmount = calculateActualAmount(updatedCartItems)
      state.cartItemsPayment = cartItemsPayment(updatedCartItems)
      saveToStorage(updatedCartItems, 'cart')
    },
    /**
     * cập nhật tên sản phẩm theo ngôn ngữ
     */
    updateCartItemName: (state, action: PayloadAction<{ id: number; name: string; unit: string | null }[]>) => {
      const nameUpdates = new Map(action.payload.map((item) => [item.id, item]))

      state.cartItems = state.cartItems.map((cart: CartItem) => (nameUpdates.has(cart.id) ? { ...cart, ...nameUpdates.get(cart.id) } : cart))

      saveToStorage(state.cartItems, 'cart')
    },
    /**
     * cập nhật phí nếu có vat
     */
    updateOrderVat: (state, action: PayloadAction<{ vat_value: { [key: string]: string | number }; increase_price?: string; vat?: string }>) => {
      // * CŨ: const increasePrice = action.payload.increase_price ? 1 + +action.payload.increase_price / 100 : 1
      const increasePrice = 1 // * khi yêu cầu xuất hóa đơn sẽ không tăng % giá mỗi sản phẩm nữa

      if (action.payload.vat_value.vat === 1) {
        const vat = action.payload.vat ? +action.payload.vat : 0

        const actualAmount = calculateActualAmount(updatePriceField(state.cartItems, Boolean(state.order.delivery_global_id) == true, increasePrice))
        const taxFee = Math.round((actualAmount + state.order.transport_fee) * vat)
        const transactionFee = calculateTransactionFee(state.payments, state.order, state.cartItems, action.payload)

        state.actualAmount = actualAmount
        state.taxFee = taxFee
        state.order.taxvalue = vat
        state.order.transaction_fee = Math.round(transactionFee)
        state.order.use_bonus = 0
      } else if (action.payload.vat_value.vat === 0) {
        const actualAmount = calculateActualAmount(updatePriceField(state.cartItems, Boolean(state.order.delivery_global_id) == true))
        const transactionFee = calculateTransactionFee(state.payments, state.order, state.cartItems, action.payload)

        state.actualAmount = actualAmount
        state.taxFee = 0
        state.order.taxvalue = 0
        state.order.transaction_fee = Math.round(transactionFee)
      }

      state.order = { ...state.order, ...action.payload.vat_value }

      // Lưu vào sessionStorage nếu có window
      if (typeof window !== 'undefined') {
        sessionStorage.setItem('order', JSON.stringify(state.order))
      }
    },
    /**
     * thêm lỗi vat
     */
    updateVatError: (state, action: PayloadAction<Record<string, string>>) => {
      state.vatError = action.payload
    },
    /**
     * cập nhật thời gian lấy hàng
     */
    updatePickupTime: (state, action: PayloadAction<{ type: 'datePickup' | 'timePickup'; value: string | undefined }>) => {
      // có thể thêm hoặc xóa đi
      state[action.payload.type] = action.payload.value
    },
    /**
     * clear giỏ hàng
     */
    clearCart: (state, action: PayloadAction<{ all: boolean }>) => {
      const { all } = action.payload

      if (all) {
        state.cartItems = []
        state.cartItemsPayment = []
        state.totalMoney = 0
        state.actualAmount = 0
        saveToStorage(state.cartItems, 'cart')
        return
      }

      state.cartItems = state.cartItems.filter((item) => item.isCheck === false)
      state.cartItemsPayment = cartItemsPayment(state.cartItems)
      state.totalMoney = calculateTotal(state.cartItems)
      state.actualAmount = calculateActualAmount(updatePriceField(state.cartItems, Boolean(state.order.delivery_global_id) == true))
      saveToStorage(state.cartItems, 'cart')
    },
    clearOrder: (state) => {
      state.order = orderTemplate
      sessionStorage.setItem('order', JSON.stringify({}))
    },
    deleteItemFromCart: (state, action: PayloadAction<number[]>) => {
      state.cartItems = state.cartItems.filter((cart: CartItem) => !action.payload.includes(cart.id))
      state.cartItemsPayment = cartItemsPayment(state.cartItems)
      state.totalMoney = calculateTotal(state.cartItems)
      state.actualAmount = calculateActualAmount(updatePriceField(state.cartItems, Boolean(state.order.delivery_global_id) == true))
      saveToStorage(state.cartItems, 'cart')
    },
    setIsNew: (state, action: PayloadAction<boolean>) => {
      state.isNew = action.payload
    },
    syncCartFromTabs: (state, action: PayloadAction<CartItem[]>) => {
      state.cartItems = action.payload
      state.cartItemsPayment = cartItemsPayment(state.cartItems)
      state.totalMoney = calculateTotal(state.cartItems)
      state.actualAmount = calculateActualAmount(updatePriceField(state.cartItems, Boolean(state.order.delivery_global_id) == true))
    },
    selectOne: (state, action: PayloadAction<{ id: number }>) => {
      state.cartItems = state.cartItems.map((cart: CartItem) => (cart.id === action.payload.id ? { ...cart, isCheck: true } : { ...cart, isCheck: false }))
      state.actualAmount = calculateActualAmount(state.cartItems)
      state.cartItemsPayment = cartItemsPayment(state.cartItems)
      saveToStorage(state.cartItems, 'cart')
    },
  },
})

export const {
  setOrder,
  removeItem,
  updateCheck,
  addItem,
  storeWhenLogin,
  storeWhenRebuy,
  selectOne,
  updateQuantity,
  updateCategoryExtraId,
  updateCartItemName,
  updateOrderVat,
  updateVatError,
  updateOrder,
  updatePayments,
  updatePickupTime,
  updateOrderNote,
  clearCart,
  clearOrder,
  deleteItemFromCart,
  setIsNew,
  syncCartFromTabs,
} = orderSlice.actions
export default orderSlice.reducer
